import React, { useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { HbrSpinner } from '@harbor/elements-react-standalone';
import { OrgChartRow, isManagerEmail, getDirectReports } from '../../../support/orgScope';

import PovTeamMembersTable from './PovTeamMembersTable';
import PovMetricsDashboard from './PovMetricsDashboard';
import { useSfTeamPovActivitySummary, povActivityByReport } from '../../../hooks/useSfTeamPovActivitySummary';

import LeaderTileStrip from '../LeaderTileStrip';
import StbAccordion from '../../security-toolbox-ui/StbAccordion';

import { useFetchDashboardPlacements, useFetchDashboardTileLinks } from '../../../hooks/toolbox-hooks';
import type { DashboardPlacement, DashboardTileLink } from '../../../types/dashboard';
import { TileRuntimeContext } from '../../../support/tileRuntimeContext';

interface Props {
  orgRows: OrgChartRow[];
  managerEmail: string;
  onSelectPerson: (email: string) => void;
  /** The time dropdown's `current_time_filter` (`all` or `range:...`); opportunities are kept by Close Date. */
  timeFilter?: string;
  /** The period in words (e.g. "This Fiscal Quarter"), for headings. */
  timeShort?: string;
  /** The fiscal calendar is still loading; hold the data until the window is known. */
  timePending?: boolean;
  enableDebugLogging?: boolean;
}

const COMPONENT_KEY_TEAM = 'Leader.PovTeamSummaryPage';
const REGION_KEY_DASHBOARD = 'dashboard';
const REGION_KEY_DASHBOARD_SECONDARY = 'dashboard_secondary';

function getPlacementLayoutOptions(placement: any): any {
  return placement?.layout?.options ?? placement?.placement_layout?.options ?? placement?.options ?? {};
}

function getGridColumns(placement: any, fallback: number) {
  const opts = getPlacementLayoutOptions(placement);
  return Number(opts?.grid?.columns ?? fallback);
}

function getContainerHeight(placement: any, fallback: number) {
  const opts = getPlacementLayoutOptions(placement);
  return Number(opts?.container?.height ?? fallback);
}

function getContainerPadding(placement: any, fallback: number) {
  const opts = getPlacementLayoutOptions(placement);
  return Number(opts?.container?.padding ?? fallback);
}

function getGridGap(placement: any, fallback: number) {
  const opts = getPlacementLayoutOptions(placement);
  return Number(opts?.grid?.gap ?? opts?.container?.gap ?? fallback);
}

export default function PovTeamSummaryPage({
                                             orgRows,
                                             managerEmail,
                                             onSelectPerson,
                                             timeFilter = 'all',
                                             timeShort,
                                             timePending = false,
                                             enableDebugLogging = false,
                                           }: Props) {
  const [isDashboardOpen, setIsDashboardOpen] = useState(true);
  const [isMetricsOpen, setIsMetricsOpen] = useState(true);

  const normalizedManagerEmail = String(managerEmail || '').trim().toLowerCase();

  // Opportunities / Opps w/ POV Activity / POV SE Activities columns: opportunities closing in the period and
  // their POC/POV SE activities. A failure shows "—" in the table and a note in the dashboard.
  const { data: activitySummary, isLoading: isLoadingActivity, error: activityError } = useSfTeamPovActivitySummary(
    normalizedManagerEmail,
    timeFilter,
    { enabled: !!normalizedManagerEmail && !timePending },
  );
  const activityByEmail = useMemo(
    () => povActivityByReport(activitySummary?.tree ?? null),
    [activitySummary],
  );

  const {
    data: placements,
    isLoading: isLoadingPlacements,
    error: placementsError,
  } = useFetchDashboardPlacements(
    { component_key: COMPONENT_KEY_TEAM, region_key: REGION_KEY_DASHBOARD, enabled: true },
    { placeholderData: keepPreviousData, refetchOnWindowFocus: false }
  );

  const mainPlacement: DashboardPlacement | null = useMemo(() => {
    const arr = Array.isArray(placements) ? placements : [];
    return arr.length ? arr[0] : null;
  }, [placements]);

  const {
    data: tileLinks,
    isLoading: isLoadingLinks,
    error: linksError,
  } = useFetchDashboardTileLinks(
    mainPlacement?.id ? { placement: mainPlacement.id, enabled: true, order_by: 'order' } : undefined,
    { enabled: !!mainPlacement?.id, placeholderData: keepPreviousData, refetchOnWindowFocus: false }
  );

  const {
    data: secondaryPlacements,
    isLoading: isLoadingSecondaryPlacements,
    error: secondaryPlacementsError,
  } = useFetchDashboardPlacements(
    { component_key: COMPONENT_KEY_TEAM, region_key: REGION_KEY_DASHBOARD_SECONDARY, enabled: true },
    { placeholderData: keepPreviousData, refetchOnWindowFocus: false }
  );

  const secondaryPlacement: DashboardPlacement | null = useMemo(() => {
    const arr = Array.isArray(secondaryPlacements) ? secondaryPlacements : [];
    return arr.length ? arr[0] : null;
  }, [secondaryPlacements]);

  const {
    data: secondaryTileLinks,
    isLoading: isLoadingSecondaryLinks,
    error: secondaryLinksError,
  } = useFetchDashboardTileLinks(
    secondaryPlacement?.id ? { placement: secondaryPlacement.id, enabled: true, order_by: 'order' } : undefined,
    { enabled: !!secondaryPlacement?.id, placeholderData: keepPreviousData, refetchOnWindowFocus: false }
  );

  const runtimeContext: TileRuntimeContext = useMemo(() => {
    const directReports = getDirectReports(orgRows, normalizedManagerEmail);
    const teamEmails = directReports.map((r) => String(r.user_email || '').toLowerCase());

    const context: TileRuntimeContext = {
      email: normalizedManagerEmail,
      managerEmail: normalizedManagerEmail,
      domain: 'salesforce' as const,
      teamScope: teamEmails,
      teamEmailsList: teamEmails.join(','),
      teamSize: teamEmails.length,
      viewMode: 'team' as const,
      isTeamView: true,
      isIndividualView: false,
      current_time_filter: timeFilter,
    };

    if (enableDebugLogging) {
      console.log('PovTeamSummaryPage: Runtime Context', context);
    }

    return context;
  }, [orgRows, normalizedManagerEmail, timeFilter, enableDebugLogging]);

  if (!isManagerEmail(orgRows, normalizedManagerEmail)) {
    return <div className="hbr-css__text-secondary-sm">Selected user is not a manager.</div>;
  }

  if (placementsError) {
    return <div style={{ padding: 12, color: 'red' }}>{(placementsError as Error).message}</div>;
  }
  if (linksError) {
    return <div style={{ padding: 12, color: 'red' }}>{(linksError as Error).message}</div>;
  }
  if (secondaryPlacementsError) {
    return <div style={{ padding: 12, color: 'red' }}>{(secondaryPlacementsError as Error).message}</div>;
  }
  if (secondaryLinksError) {
    return <div style={{ padding: 12, color: 'red' }}>{(secondaryLinksError as Error).message}</div>;
  }

  const mainColumns = getGridColumns(mainPlacement, 1);
  const secondaryColumns = getGridColumns(secondaryPlacement, 1);

  const mainHeight = getContainerHeight(mainPlacement, 350);
  const secondaryHeight = getContainerHeight(secondaryPlacement, 260);

  const mainPadding = getContainerPadding(mainPlacement, 12);
  const secondaryPadding = getContainerPadding(secondaryPlacement, 12);

  const mainGap = getGridGap(mainPlacement, 12);
  const secondaryGap = getGridGap(secondaryPlacement, 12);

  const mainTileLinks = Array.isArray(tileLinks) ? (tileLinks as DashboardTileLink[]) : [];
  const secondaryLinks = Array.isArray(secondaryTileLinks) ? (secondaryTileLinks as DashboardTileLink[]) : [];

  const hasMainTiles = !!mainPlacement && mainTileLinks.length > 0;
  const hasSecondaryTiles = !!secondaryPlacement && secondaryLinks.length > 0;
  const hasAnyTiles = hasMainTiles || hasSecondaryTiles;

  if (enableDebugLogging) {
    console.log('PovTeamSummaryPage: Tile Links', {
      mainPlacement,
      tileLinks: mainTileLinks,
      mainLinksCount: mainTileLinks.length,
      mainColumns,
      mainHeight,
      mainPadding,
      mainGap,
      secondaryPlacement,
      secondaryTileLinks: secondaryLinks,
      secondaryLinksCount: secondaryLinks.length,
      secondaryColumns,
      secondaryHeight,
      secondaryPadding,
      secondaryGap,
    });
  }

  return (
    <div className="flex flex-col gap-4 h-full min-h-0 min-w-0" style={{ overflowY: 'auto' }}>
      {hasAnyTiles && (
        <StbAccordion
          variant="minimal"
          label={mainPlacement?.label || secondaryPlacement?.label || 'POV Summary Dashboard'}
          isOpen={isDashboardOpen}
          toggleIsOpen={setIsDashboardOpen}
        >
          <div
            className="flex flex-col gap-3"
            style={{
              minHeight: 0,
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            {isLoadingPlacements ||
            isLoadingLinks ||
            isLoadingSecondaryPlacements ||
            isLoadingSecondaryLinks ? (
              <HbrSpinner>Loading dashboard…</HbrSpinner>
            ) : (
              <>
                {hasMainTiles && (
                  <div
                    className="flex flex-col gap-3"
                    style={{
                      height: mainHeight,
                      minHeight: 0,
                      minWidth: 0,
                      padding: mainPadding,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      className="grid"
                      style={{
                        height: '100%',
                        minHeight: 0,
                        minWidth: 0,
                        gridTemplateColumns: `repeat(${Math.max(1, mainColumns)}, minmax(0, 1fr))`,
                        gap: mainGap,
                      }}
                    >
                      {mainTileLinks
                        .slice()
                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id)
                        .map((link) => {
                          const tileName = link?.tile?.name;
                          if (!tileName) return null;

                          const colSpan = Number((link.options as any)?.colSpan ?? 1);
                          const minHeight = Number((link.options as any)?.minHeight ?? 0);

                          if (enableDebugLogging) {
                            console.log(`PovTeamSummaryPage: Rendering main tile ${tileName}`, {
                              link,
                              colSpan,
                              minHeight,
                              tileSettings: link.tile?.settings,
                            });
                          }

                          return (
                            <div
                              key={`pov-main-tile-${link.id}`}
                              className="min-w-0 flex flex-col"
                              style={{
                                minHeight,
                                height: '100%',
                                minWidth: 0,
                                gridColumn: `span ${Math.max(1, Math.min(mainColumns, colSpan))}`,
                              }}
                            >
                              <LeaderTileStrip
                                tileNames={[tileName]}
                                runtimeContext={runtimeContext}
                                className="h-full min-w-0"
                                enableDebugLogging={enableDebugLogging}
                              />
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {hasSecondaryTiles && (
                  <div
                    className="flex flex-col gap-3"
                    style={{
                      height: secondaryHeight,
                      minHeight: 0,
                      minWidth: 0,
                      padding: secondaryPadding,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      className="grid"
                      style={{
                        height: '100%',
                        minHeight: 0,
                        minWidth: 0,
                        gridTemplateColumns: `repeat(${Math.max(1, secondaryColumns)}, minmax(0, 1fr))`,
                        gap: secondaryGap,
                      }}
                    >
                      {secondaryLinks
                        .slice()
                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id)
                        .map((link) => {
                          const tileName = link?.tile?.name;
                          if (!tileName) return null;

                          const colSpan = Number((link.options as any)?.colSpan ?? 1);
                          const minHeight = Number((link.options as any)?.minHeight ?? 0);

                          if (enableDebugLogging) {
                            console.log(`PovTeamSummaryPage: Rendering secondary tile ${tileName}`, {
                              link,
                              colSpan,
                              minHeight,
                              tileSettings: link.tile?.settings,
                            });
                          }

                          return (
                            <div
                              key={`pov-secondary-tile-${link.id}`}
                              className="min-w-0 flex flex-col"
                              style={{
                                minHeight,
                                height: '100%',
                                minWidth: 0,
                                gridColumn: `span ${Math.max(1, Math.min(secondaryColumns, colSpan))}`,
                              }}
                            >
                              <LeaderTileStrip
                                tileNames={[tileName]}
                                runtimeContext={runtimeContext}
                                className="h-full min-w-0"
                                enableDebugLogging={enableDebugLogging}
                              />
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </StbAccordion>
      )}

      <StbAccordion
        variant="minimal"
        label={timeShort && timeFilter !== 'all' ? `POV Metrics \u2014 ${timeShort}` : 'POV Metrics'}
        isOpen={isMetricsOpen}
        toggleIsOpen={setIsMetricsOpen}
      >
        <PovMetricsDashboard
          tree={activityError ? null : (activitySummary?.tree ?? null)}
          loading={timePending || isLoadingActivity}
          periodLabel={timeShort || 'All Time'}
        />
      </StbAccordion>

      <div className="hbr-css__container-card p-3 w-full" style={{ flex: '0 0 auto', minWidth: 0 }}>
        <div className="hbr-css__text-primary-sm-semibold mb-2">
          {timeShort && timeFilter !== 'all' ? `POV Team Members \u2014 ${timeShort}` : 'POV Team Members'}
        </div>

        <PovTeamMembersTable
          orgRows={orgRows}
          managerEmail={normalizedManagerEmail}
          activityByEmail={activityByEmail}
          activityLoading={timePending || isLoadingActivity}
          onSelectEmail={onSelectPerson}
        />
      </div>
    </div>
  );
}